/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Header, Icon, Button, Tab, Loader, Segment } from "semantic-ui-react";
import { BasicModal, CustomTable } from "../../../components/Shared";
import { AcademicCycle, Grade, Student } from "../../../api";
import { useAuth } from "../../../hooks";
import {
  AttendanceForm,
  MassiveAttendanceForm,
  AttendanceHistory,
} from "../../../components/Admin/Attendance";
import "./AttendanceGeneral.scss";

const academicCycleController = new AcademicCycle();
const gradeController = new Grade();
const studentController = new Student();

export function AttendanceGeneral() {
  const [academicCycle, setAcademicCycle] = useState(null);
  const [grades, setGrades] = useState([]);
  const [students, setStudents] = useState({});
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);

  // Estados para modales
  const [showMassiveModal, setShowMassiveModal] = useState(false);
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);

  const { accessToken } = useAuth();
  const { academicCycleId } = useParams();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        // Obtener información del ciclo académico
        const cycleResponse = await academicCycleController.getAcademicCycle(
          academicCycleId
        );
        setAcademicCycle(cycleResponse);

        // Obtener grados del nivel del ciclo académico
        const gradesResponse = await gradeController.getGradesByLevelId(
          cycleResponse.levelId,
          accessToken,
        );
        setGrades(gradesResponse);

        // Obtener estudiantes por grado
        const studentsData = {};
        for (const grade of gradesResponse) {
          try {
            const studentsResponse = await studentController.getStudents(
              accessToken,
              grade.id,
              academicCycleId,
              {},
              1,
              1000,
            );
            // La API devuelve { data: [...], pagination: {...} }
            studentsData[grade.id] = studentsResponse.data || [];
          } catch (error) {
            console.error(
              `Error fetching students for grade ${grade.id}:`,
              error,
            );
            studentsData[grade.id] = [];
          }
        }
        setStudents(studentsData);
      } catch (error) {
        console.error("Error loading attendance data:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [academicCycleId, accessToken, reload]);

  // Funciones para manejar modales
  const onReload = () => setReload((prev) => !prev);

  const openMassiveAttendanceModal = (grade) => {
    setSelectedGrade(grade);
    setShowMassiveModal(true);
  };

  const openIndividualAttendanceModal = (student, grade) => {
    setSelectedStudent(student);
    setSelectedGrade(grade);
    setShowIndividualModal(true);
  };

  const openAttendanceHistoryModal = (student, grade) => {
    setSelectedStudent(student);
    setSelectedGrade(grade);
    setShowHistoryModal(true);
  };

  const closeMassiveModal = () => {
    setShowMassiveModal(false);
    setSelectedGrade(null);
  };

  const closeIndividualModal = () => {
    setShowIndividualModal(false);
    setSelectedStudent(null);
    setSelectedGrade(null);
  };

  const closeHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedStudent(null);
    setSelectedGrade(null);
  };

  const getTableConfig = () => ({
    headers: ["DNI", "Estudiante", "Correo electrónico", "Estado", "Acciones"],
    fields: [
      {
        field: "User.dni",
        dataType: "text",
      },
      {
        component: "custom",
        render: (student) => (
          <span>
            {student.User.lastname} {student.User.secondLastname}{" "}
            {student.User.firstname}
          </span>
        ),
      },
      {
        field: "User.email",
        dataType: "text",
      },
      {
        component: "custom",
        render: (student) => (
          <span>
            <Icon
              name={student.status === 1 ? "check circle" : "times circle"}
              color={student.status === 1 ? "green" : "red"}
            />
            {student.status === 1 ? "Activo" : "Inactivo"}
          </span>
        ),
      },
      {
        component: "custom",
        align: "center",
        render: (student) => (
          <div
            style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}
          >
            <Button
              size="small"
              color="blue"
              icon="chart line"
              content="Ver Asistencia"
              onClick={() =>
                openAttendanceHistoryModal(
                  student,
                  grades.find((g) => g.id === student.gradeId),
                )
              }
            />
            <Button
              size="small"
              color="green"
              icon="plus"
              content="Registrar"
              onClick={() =>
                openIndividualAttendanceModal(
                  student,
                  grades.find((g) => g.id === student.gradeId),
                )
              }
            />
          </div>
        ),
      },
    ],
    columnWidths: ["15%", "35%", "25%", "15%", "10%"],
  });

  const renderStudentsTable = (gradeId) => {
    const gradeStudents = students[gradeId] || [];

    if (gradeStudents.length === 0) {
      return (
        <Segment placeholder>
          <div className="attendance-general-page__empty">
            <Icon name="users" size="huge" />
            <p>No hay estudiantes registrados en este grado</p>
          </div>
        </Segment>
      );
    }

    return (
      <CustomTable
        tableConfig={getTableConfig()}
        data={gradeStudents}
        loading={false}
        striped={true}
        size="small"
      />
    );
  };

  const panes = grades.map((grade) => ({
    menuItem: grade?.name,
    render: () => (
      <Tab.Pane attached={false}>
        <div style={{ marginBottom: "1em" }}>
          <Button
            primary
            icon
            labelPosition="left"
            onClick={() => openMassiveAttendanceModal(grade)}
          >
            <Icon name="plus" />
            Registrar asistencia masiva - {grade?.name}
          </Button>
        </div>
        {renderStudentsTable(grade.id)}
      </Tab.Pane>
    ),
  }));

  if (loading) return <Loader active inline="centered" />;
  if (!academicCycle)
    return <div>Error al cargar la información del ciclo académico</div>;

  return (
    <>
      <div className="attendance-general-page">
        <Header className="attendance-general-page__header" as="h3">
          <Icon name="check circle" />
          <Header.Content>
            Asistencia General - {academicCycle.Level?.name} - {academicCycle.name}
          </Header.Content>
        </Header>

        {panes.length > 0 ? (
          <Tab
            className="attendance-general-page__filters"
            menu={{ secondary: true }}
            panes={panes}
          />
        ) : (
          <Segment placeholder>
            <Header icon>
              <Icon name="users" />
              No hay grados disponibles para este ciclo académico
            </Header>
          </Segment>
        )}
      </div>

      {/* Modal para registro masivo de asistencia */}
      <BasicModal
        show={showMassiveModal}
        close={closeMassiveModal}
        size="large"
        title={`Registro Masivo de Asistencia - ${selectedGrade?.name}`}
      >
        {selectedGrade && (
          <MassiveAttendanceForm
            onClose={closeMassiveModal}
            onReload={onReload}
            students={students[selectedGrade.id] || []}
            gradeName={selectedGrade.name}
            gradeId={selectedGrade.id}
            academicCycleId={academicCycleId}
            academicCycle={academicCycle}
          />
        )}
      </BasicModal>

      {/* Modal para registro individual de asistencia */}
      <BasicModal
        show={showIndividualModal}
        close={closeIndividualModal}
        size="small"
        title={`Registrar Asistencia Individual - ${selectedStudent?.User.lastname} ${selectedStudent?.User.secondLastname} ${selectedStudent?.User.firstname} (${selectedStudent?.User.dni})`}
      >
        {selectedStudent && selectedGrade && (
          <AttendanceForm
            onClose={closeIndividualModal}
            onReload={onReload}
            student={selectedStudent}
            gradeId={selectedGrade.id}
            academicCycleId={academicCycleId}
            academicCycle={academicCycle}
          />
        )}
      </BasicModal>

      {/* Modal para historial de asistencia */}
      <BasicModal
        show={showHistoryModal}
        close={closeHistoryModal}
        size="large"
        title={`Historial de Asistencia - ${selectedStudent?.User?.lastname} ${selectedStudent?.User?.secondLastname} ${selectedStudent?.User?.firstname} (${selectedStudent?.User?.dni})`}
      >
        {selectedStudent && selectedGrade && (
          <AttendanceHistory
            onClose={closeHistoryModal}
            student={selectedStudent}
            gradeId={selectedGrade.id}
            academicCycleId={academicCycleId}
            academicCycle={academicCycle}
          />
        )}
      </BasicModal>
    </>
  );
}
